import AddIcon from '@mui/icons-material/Add';
import DoDisturbOnOutlinedIcon from '@mui/icons-material/DoDisturbOnOutlined';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Backdrop,
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
} from '@mui/material';
import {t} from 'i18next';
import {useEffect, useState} from 'react';

import API from '../../../api/axios';
import {apiBaseUrl} from '../../../api/urls';
import {
  Backhauler,
  BackhaulerListResponse,
} from '../../../interfaces/Backhauler';
import {DashboardPanelData} from '../../../interfaces/Dashboard';
import {BackHaulersBulkActionButton} from '../../backHaulers/buttons/BackHaulersBulkActionButton';
import BackHaulersItemUpsertButton from '../../backHaulers/buttons/BackHaulersItemUpsertButton';
import AccessControl from '../../common/AccessControl';
import {usePanel} from '../../dashboards/entities/DashboardEntityContext';
import {DashboardPanelTitleSlot} from '../DashboardPanelTitleSlot';
import {BackHaulersTabs} from './BackHaulersTabs';

interface Props {
  value?: DashboardPanelData;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const BackHaulers = ({value}: Props) => {
  /*********/
  /* fetch */
  /*********/
  const [fetchedData, setFetchedData] = useState<Backhauler[]>();
  const [fetchedErrors, setFetchedErrors] = useState<string[]>([]);
  const [fetchedInProgress, setFetchedInProgress] = useState(false);
  const [activeId, setActiveId] = useState<number | null>(() => null);
  const [refreshToken, setRefreshToken] = useState(0);

  const fetchData = async () => {
    setFetchedInProgress(true);

    try {
      const resp = await API.get<BackhaulerListResponse>(
        `${apiBaseUrl}/backhauler`,
        {}
      );
      setFetchedData(resp.data.items);
    } catch (error: any) {
      setFetchedErrors(error.response.data?.errors ?? []);
    }

    setFetchedInProgress(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleOnBulkSubmitted = () => {
    fetchData();
    setRefreshToken((v) => v + 1);
  };

  const handleOnDeleted = () => {
    setActiveId(null);
    fetchData();
  };

  const [panel] = usePanel();

  return (
    <Box
      display="flex"
      flexDirection="column"
      height="100%"
      width="100%"
      overflow="hidden"
    >
      <DashboardPanelTitleSlot>
        {t(`panels.${panel?.code}`)}
      </DashboardPanelTitleSlot>

      <Backdrop open={fetchedInProgress} sx={{position: 'absolute'}}>
        <CircularProgress color="inherit" />
      </Backdrop>

      {fetchedErrors.map((error, index) => (
        <Alert key={index} severity="error" sx={{mb: 2}}>
          {error}
        </Alert>
      ))}

      <ButtonGroup
        sx={{
          position: 'absolute',
          top: 4,
          left: 4,
          p: 1,
        }}
      >
        <BackHaulersItemUpsertButton
          component={Button}
          componentProps={{
            color: 'primary',
            size: 'small',
          }}
          onSubmitted={() => fetchData()}
        >
          <AddIcon />
        </BackHaulersItemUpsertButton>

        <AccessControl permissions={['patch::/backhauler/start-all']}>
          <BackHaulersBulkActionButton
            action="start"
            componentProps={{
              size: 'small',
              title: 'Start all backhaulers',
              startIcon: <PlayCircleOutlineIcon />,
            }}
            onSubmitted={handleOnBulkSubmitted}
          >
            Start all
          </BackHaulersBulkActionButton>
        </AccessControl>

        <AccessControl permissions={['patch::/backhauler/stop-all']}>
          <BackHaulersBulkActionButton
            action="stop"
            confirm
            componentProps={{
              size: 'small',
              title: 'Stop all backhaulers',
              startIcon: <DoDisturbOnOutlinedIcon />,
            }}
            onSubmitted={handleOnBulkSubmitted}
          >
            Stop all
          </BackHaulersBulkActionButton>
        </AccessControl>

        <Button onClick={fetchData}>
          <RefreshIcon />
        </Button>
      </ButtonGroup>

      <Box width="100%" height="100%" sx={{pt: 0}}>
        {fetchedData ? (
          <BackHaulersTabs
            value={fetchedData}
            activeId={activeId}
            setActiveId={setActiveId}
            refreshToken={refreshToken}
            onDeleted={handleOnDeleted}
          />
        ) : (
          <Box
            height="100%"
            alignItems="center"
            justifyContent="center"
            display="flex"
          >
            No data
          </Box>
        )}
      </Box>
    </Box>
  );
};
